<template>
  <div class="lf-icon" :class="{ 'lf-icon--has-action': hasAction }" :style="iconWrapperStyles">
    <span class="lf-icon__shape" :style="iconStyles" @click="onClickIcon">
      <i :class="model.state.iconClasses"></i>
    </span>
  </div>
</template>

<script lang="ts">
import type { CSSProperties, PropType } from 'vue';
import { onBeforeUnmount } from 'vue';
import { computed, defineComponent, onMounted, watch } from 'vue';
import hotkeys from 'hotkeys-js';
import type { AddonLfIconModel } from '@/src/components/addons/lf-icon/Model';
import { IconDisplayType, IconShapeType, KeyBinding } from '@/src/typings/enums/enums';
import { useCampaignStore } from '@/src/store/campaign';

export default defineComponent({
  name: 'AddonLfIcon',
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<AddonLfIconModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();

    const onClickIcon = (event?: Event) => {
      if (campaignStore.model?.state.isEditModeActive) {
        return;
      }

      // We should only stop propagation if we actually have an action.
      if (event && props.model.state.action?.state.type) {
        event.stopPropagation();
      }

      props.model.state?.action?.triggerAction(props.model);
    };

    const hasAction = computed(() => {
      if (props.model.state.action && props.model.state.action.state) {
        return !!props.model.state.action?.state;
      }

      return false;
    });

    const onKeybindingPress = (e: KeyboardEvent) => {
      onClickIcon(e);
    };

    const unbindKeybindings = () => {
      hotkeys.unbind('escape', onKeybindingPress);
      hotkeys.unbind('enter', onKeybindingPress);
    };

    const initKeybindings = () => {
      // Unbind the keybinding before we bind new ones.
      unbindKeybindings();

      if (props.model.state.action?.state.keybinding) {
        switch (props.model.state.action?.state.keybinding) {
          case KeyBinding.ENTER:
            hotkeys('enter', onKeybindingPress);
            break;

          case KeyBinding.ESCAPE:
            hotkeys('escape', onKeybindingPress);
            break;
        }
      }
    };

    const iconWrapperStyles = computed<CSSProperties>(() => {
      return {
        textAlign: props.model.state.settings.layout?.alignment ?? 'center'
      };
    });

    const iconForegroundColor = computed(() => {
      const layout = props.model.state.settings.layout;

      let color = campaignStore.model?.state.layout?.textColor || campaignStore.model?.state.globalLayout?.textColor;

      const sectionColorOverwrite =
        !props.model.getSection().state.config.settings.state.style.content?.useDefaultTextColor ||
        props.model.getSection().state.config.settings.state.style.content?.useDefaultTextColor !== undefined;

      if (sectionColorOverwrite && props.model.getSection().state.config.settings.state.style.content?.color) {
        color = props.model.getSection().state.config.settings.state.style.content?.color;
      }
      if (layout?.color) {
        color = layout?.color;
      }

      return color;
    });

    const iconSize = computed(() => {
      const layout = props.model.state.settings.layout;
      let height: string | undefined;
      let width: string | undefined;
      if (layout?.display === IconDisplayType.FRAMED || layout?.display === IconDisplayType.STACKED) {
        height = '27.5px';
        width = '27.5px';
        if (layout.size) {
          height = layout.sizeIsPercentage ? '1.7em' : layout?.size * 1.7 + 'px'; // If percentage, force correct size (TODO: Speak with Daugaard on why it forces 1.7em (from the old NG project) as it's not clear)
          width = layout.sizeIsPercentage ? '1.7em' : layout?.size * 1.7 + 'px'; // If percentage, force correct size (TODO: Speak with Daugaard on why it forces 1.7em (from the old NG project) as it's not clear)
        }
      }
      let sizeMeasurement = 'px';
      if (layout?.sizeIsPercentage) {
        sizeMeasurement = '%';
      }

      return {
        width,
        height,
        fontSize: `${layout?.size}${sizeMeasurement}`
      };
    });

    const iconFramed = computed<CSSProperties>(() => {
      const isCircle =
        props.model.state.settings.layout?.shape === IconShapeType.CIRCLE ||
        props.model.state.settings.layout?.shape === undefined;

      return {
        color: iconForegroundColor.value,
        border: `2px solid ${iconForegroundColor.value}`,
        ...iconSize.value,
        ...(isCircle && { borderRadius: '50%' })
      };
    });

    const iconStacked = computed<CSSProperties>(() => {
      const layout = props.model.state.settings.layout;
      const isCircle =
        props.model.state.settings.layout?.shape === IconShapeType.CIRCLE ||
        props.model.state.settings.layout?.shape === undefined;

      return {
        backgroundColor: iconForegroundColor.value,
        color:
          layout?.stackedColor ||
          campaignStore.model?.state.layout?.backgroundColor ||
          campaignStore.model?.state.globalLayout?.backgroundColor,
        ...iconSize.value,
        ...(isCircle && { borderRadius: '50%' })
      };
    });

    const iconStyles = computed(() => {
      if (props.model.state.settings.layout?.display === IconDisplayType.FRAMED) {
        return iconFramed.value;
      }

      if (props.model.state.settings.layout?.display === IconDisplayType.STACKED) {
        return iconStacked.value;
      }

      return {
        color: iconForegroundColor.value,
        ...iconSize.value
      };
    });

    onMounted(() => {
      props.model.state.action?.startAutoTrigger(onClickIcon);
    });

    onBeforeUnmount(unbindKeybindings);

    watch(() => props.model.state.action?.state?.keybinding, initKeybindings, { immediate: true });

    return {
      onClickIcon,
      hasAction,
      iconWrapperStyles,
      iconStyles
    };
  }
});
</script>
